import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Popover,
  Divider,
  TextField
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import FMSToolbar from 'src/common/FMSToolbar';
import FilterSelectInput from 'src/common/FilterSelectInput';
import TransactionListTable from 'src/components/transactions/TransactionListTable';
import { setTransactionType } from 'src/redux/transactionSlice';
import { getAllTanks } from 'src/redux/tankSlice';
import NewTransaction from 'src/components/transactions/NewTransaction';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import DateFilterField from 'src/common/formFields/DateFilterField';

import {
  DATE_FORMAT,
  REPORT_FILTER_OPTIONS,
  TRANSACTION_TYPES,
  USER_ROLES
} from 'src/utils/constants';

import {
  reportFormModel,
  reportFormInitialValues,
  reportValidationSchema
} from 'src/components/reports/reportFormModel';

import {
  inwardReportFormModel,
  inwardReportFormInitialValues,
  inwardReportValidationSchema
} from 'src/components/reports/inwardReportFormModel';
import {
  externalReportFormModel,
  externalReportFormInitialValues,
  externalReportValidationSchema
} from 'src/components/reports/externalReportFormModel';

import {
  getFilterOptions,
  getReportDownloadURL,
  getReportFormTitle,
  getSearchPlaceholder
} from 'src/components/transactions/transactionService';
import useAuth from 'src/context/AuthContext';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { FLEET_ONLY_TRANSACTION_TYPE, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import {
  getStoredUserTimezone,
  nowInTimezone,
  parseInStoredTimezone
} from 'src/utils/timezoneUtils';

const EMPTY_DATE_RANGE = { start: null, end: null };

const Transactions = ({ location }) => {
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const { transactionType, transactionList } = useSelector((state) => state.transactions);
  const { allTanks = [] } = useSelector((state) => state.tanks);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);

  const [openModal, setOpenModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterDateRange, setFilterDateRange] = useState(EMPTY_DATE_RANGE);
  const [tankFilter, setTankFilter] = useState('all');
  const [externalAppliedVehicle, setExternalAppliedVehicle] = useState('');
  const [externalAppliedDateRange, setExternalAppliedDateRange] = useState(EMPTY_DATE_RANGE);
  const [externalAppliedSite, setExternalAppliedSite] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftVehicle, setDraftVehicle] = useState('');
  const [draftStartDateTime, setDraftStartDateTime] = useState(null);
  const [draftEndDateTime, setDraftEndDateTime] = useState(null);
  const [draftSite, setDraftSite] = useState('all');
  const [draftTank, setDraftTank] = useState('all');
  const [filterError, setFilterError] = useState('');
  const effectiveTransactionType = isFleetOnlyMode ? FLEET_ONLY_TRANSACTION_TYPE : transactionType;
  const isSales = effectiveTransactionType !== TRANSACTION_TYPES[1].id;
  const isIssued = effectiveTransactionType === TRANSACTION_TYPES[0].id;
  const isInward = effectiveTransactionType === TRANSACTION_TYPES[1].id;
  const isExternal = effectiveTransactionType === TRANSACTION_TYPES[3].id;
  const canAddTransaction = isInward ? hasAdminAccess || isApprover : hasAdminAccess;
  const isDropdownFilterView = isExternal || (isIssued && isAdmin);
  let transactionDescription = null;
  if (isFleetOnlyMode) {
    transactionDescription = 'Manage fleet inward transactions only.';
  } else if (isExternal) {
    transactionDescription = 'Manage fuel inwards, issued, and transfer records.';
  }
  const appliedSearchText = isExternal ? externalAppliedVehicle : searchText;
  const appliedDateFilter = isExternal ? externalAppliedDateRange : filterDateRange;
  const appliedSiteFilter = isExternal ? externalAppliedSite : siteFilter;
  const reportDownloadUrl = getReportDownloadURL(effectiveTransactionType);
  const filterOptions = getFilterOptions(effectiveTransactionType);
  const externalReportFilterOptions = {
    ...REPORT_FILTER_OPTIONS,
    dispenser: false,
    vehicle: false,
    vehicleGroup: false,
    assetType: false
  };
  let activeReportFormModel = inwardReportFormModel;
  let activeReportValidation = inwardReportValidationSchema;
  let activeReportInitialValues = inwardReportFormInitialValues;
  if (isExternal) {
    activeReportFormModel = externalReportFormModel;
    activeReportValidation = externalReportValidationSchema;
    activeReportInitialValues = externalReportFormInitialValues;
  } else if (isSales) {
    activeReportFormModel = reportFormModel;
    activeReportValidation = reportValidationSchema;
    activeReportInitialValues = reportFormInitialValues;
  }
  const searchPlaceholder = getSearchPlaceholder(effectiveTransactionType);

  const tankOptions = useMemo(() => {
    const filteredTanks = siteFilter === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(siteFilter));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, siteFilter]);

  const draftTankOptions = useMemo(() => {
    const filteredTanks = draftSite === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(draftSite));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, draftSite]);

  const handleSearch = (value) => setSearchText(value);

  const handleGroupChange = (value) => {
    setFilterDateRange(EMPTY_DATE_RANGE);
    dispatch(setTransactionType(value));
  };

  useEffect(() => {
    if (isFleetOnlyMode && transactionType !== FLEET_ONLY_TRANSACTION_TYPE) {
      dispatch(setTransactionType(FLEET_ONLY_TRANSACTION_TYPE));
    }
  }, [dispatch, isFleetOnlyMode, transactionType]);

  useEffect(() => {
    if (isAdmin && isIssued && allTanks.length === 0) {
      dispatch(getAllTanks());
    }
  }, [allTanks.length, dispatch, isAdmin, isIssued]);

  useEffect(() => {
    if (!isIssued) {
      setTankFilter('all');
      return;
    }

    const hasSelectedTank = tankOptions.some(
      (tank) => String(tank.id) === String(tankFilter)
    );
    if (!hasSelectedTank) setTankFilter('all');
  }, [tankFilter, tankOptions, isIssued]);

  const getDateTimeFilterString = (newValue) => (
    newValue && newValue.isValid()
      ? moment.tz(
        newValue.format(DATE_FORMAT.unixDateTime),
        DATE_FORMAT.unixDateTime,
        true,
        getStoredUserTimezone()
      ).format(DATE_FORMAT.unixDateTime)
      : null
  );

  const setDateRangeField = (fieldName, newValue) => {
    setFilterDateRange((currentDateRange) => ({
      ...currentDateRange,
      [fieldName]: getDateTimeFilterString(newValue)
    }));
  };

  const clearDateRangeFilter = () => setFilterDateRange(EMPTY_DATE_RANGE);
  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  const externalVehicleOptions = useMemo(() => {
    const uniqueVehicles = Array.from(
      new Set(
        (transactionList || [])
          .map((item) => item?.vehicle_number)
          .filter(Boolean)
      )
    );
    return uniqueVehicles;
  }, [transactionList]);

  const openFilterPopover = (e) => {
    setDraftVehicle(isExternal ? externalAppliedVehicle || '' : '');
    const appliedDateRange = isExternal ? externalAppliedDateRange : filterDateRange;
    const now = nowInTimezone(getStoredUserTimezone()).seconds(0).milliseconds(0);
    setDraftStartDateTime(
      appliedDateRange.start
        ? parseInStoredTimezone(appliedDateRange.start, DATE_FORMAT.unixDateTime)
        : now.clone().subtract(1, 'hour')
    );
    setDraftEndDateTime(
      appliedDateRange.end
        ? parseInStoredTimezone(appliedDateRange.end, DATE_FORMAT.unixDateTime)
        : now
    );
    setDraftSite(isExternal ? externalAppliedSite || 'all' : siteFilter || 'all');
    setDraftTank(isExternal ? 'all' : tankFilter || 'all');
    setFilterError('');
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    if (!draftStartDateTime || !draftEndDateTime) {
      setFilterError('Start date time and end date time are required.');
      return;
    }
    if (draftEndDateTime.isBefore(draftStartDateTime)) {
      setFilterError('End date time must be after start date time.');
      return;
    }
    const appliedDateRange = {
      start: draftStartDateTime.format(DATE_FORMAT.unixDateTime),
      end: draftEndDateTime.format(DATE_FORMAT.unixDateTime)
    };
    if (isExternal) {
      setExternalAppliedVehicle(draftVehicle || '');
      setExternalAppliedDateRange(appliedDateRange);
      setExternalAppliedSite(draftSite || 'all');
    } else {
      setFilterDateRange(appliedDateRange);
      setSiteFilter(draftSite || 'all');
      setTankFilter(draftTank || 'all');
    }
    closeFilterPopover();
  };

  const handleClearDraftFilters = () => {
    setDraftVehicle('');
    setDraftStartDateTime(null);
    setDraftEndDateTime(null);
    setDraftSite('all');
    setDraftTank('all');
    setFilterError('');

    if (isExternal) {
      setExternalAppliedVehicle('');
      setExternalAppliedDateRange(EMPTY_DATE_RANGE);
      setExternalAppliedSite('all');
    } else {
      setFilterDateRange(EMPTY_DATE_RANGE);
      setSiteFilter('all');
      setTankFilter('all');
    }

    closeFilterPopover();
  };

  return (
    <>
      <Helmet>
        <title>Transactions | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: isFleetOnlyMode ? '#F3F8FC' : '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* HEADER */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: isExternal ? 'flex-start' : 'center' }}
            spacing={2}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, mb: 0.5 }}
              >
                TRANSACTIONS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={isExternal ? { whiteSpace: 'nowrap' } : undefined}
              >
                {transactionDescription || (
                  <>
                    Manage fuel inwards, issued,
                    <br />
                    and transfer records.
                  </>
                )}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{
                width: { xs: '100%', md: isExternal ? '100%' : 'auto' }
              }}
            >
              <FMSToolbar
                showSearch={!isExternal}
                showGroup={!isExternal && !isFleetOnlyMode}
                placeholder={searchPlaceholder}
                actionName="Export"
                actionIcon={<DownloadIcon />}
                groupData={isFleetOnlyMode
                  ? TRANSACTION_TYPES.filter((type) => type.id === FLEET_ONLY_TRANSACTION_TYPE)
                  : TRANSACTION_TYPES}
                groupType={effectiveTransactionType}
                groupLabel="Category"
                onGroupChange={handleGroupChange}
                onSearch={handleSearch}
                onAction={() => {
                  if (isExternal) {
                    setOpenReportModal(true);
                  } else {
                    setOpenReportModal(true);
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  '& .MuiCardContent-root': {
                    p: 1.5,
                    ...(isExternal && {
                      pl: 1.5,
                      pr: 1.5
                    })
                  },
                  '& .MuiCardContent-root > .MuiStack-root': {
                    width: '100%',
                    alignItems: { xs: 'stretch', md: isExternal ? 'stretch' : 'flex-start' },
                    ...(isExternal && {
                      justifyContent: 'space-between',
                      columnGap: 2
                    }),
                    flexWrap: { xs: 'nowrap', md: 'wrap', lg: 'nowrap' },
                    rowGap: 1.25,
                    columnGap: 1.25
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type': {
                    minWidth: 0,
                    flex: '1 1 auto',
                    width: '100%',
                    ...(isExternal && {
                      justifyContent: 'space-between'
                    }),
                    flexWrap: { xs: 'nowrap', md: 'wrap', lg: 'nowrap' },
                    rowGap: 1.25,
                    columnGap: isExternal ? 2 : 1.25
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:last-of-type': {
                    flexShrink: 0,
                    width: {
                      xs: '100%',
                      md: isExternal ? 190 : 'auto'
                    },
                  },
                  '& .MuiTextField-root': {
                    width: {
                      xs: '100%',
                      sm: '100%',
                      md: isIssued ? 320 : 240,
                      lg: isIssued ? 360 : 280
                    },
                    minWidth: {
                      md: isIssued ? 300 : 220
                    },
                    flex: {
                      md: isIssued ? '1 1 320px' : '1 1 240px'
                    }
                  },
                  '& .MuiFormControl-root': {
                    width: {
                      xs: '100%',
                      sm: '100%',
                      md: 150,
                      lg: 170
                    },
                    minWidth: {
                      md: 140
                    }
                  },
                  '& .MuiInputBase-root': {
                    height: 38
                  },
                  '& .MuiFormControl-root .MuiInputLabel-root': {
                    top: 0
                  },
                  '& .MuiFormControl-root .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '38px',
                    boxSizing: 'border-box'
                  },
                  '& .MuiButton-root': {
                    minHeight: 38
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:last-of-type .MuiButton-root': {
                    height: 38,
                    px: isInward ? 1.0 : 2.5,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    ...(isInward && {
                      minWidth: 50
                    })
                  },
                  '& .MuiButton-contained': {
                    whiteSpace: 'nowrap'
                  },
                  '& .MuiDivider-vertical': {
                    display: {
                      md: 'none',
                      lg: 'block'
                    }
                  }
                }}
              >
                {isDropdownFilterView ? (
                  <>
                    {isExternal && (
                      <FilterSelectInput
                        label="Category"
                        name="category"
                        value={effectiveTransactionType}
                        data={TRANSACTION_TYPES}
                        onInputChange={handleGroupChange}
                        sx={{
                          width: {
                            xs: '100%',
                            sm: '100%',
                            md: 150,
                            lg: 170
                          },
                          minWidth: {
                            md: 140
                          },
                          mr: 0
                        }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={openFilterPopover}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 38,
                        borderRadius: 2,
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 140,
                          lg: 180
                        },
                        minWidth: {
                          md: 130
                        },
                        whiteSpace: 'nowrap',
                        mr: 0
                      }}
                    >
                      Filters
                    </Button>
                    {canAddTransaction && !isIssued && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenModal(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          minHeight: 38,
                          px: 2,
                          borderRadius: 2,
                          width: { xs: '100%', sm: '100%', md: 'auto' },
                          flexShrink: 0
                        }}
                      >
                        Add Transaction
                      </Button>
                    )}
                    <Popover
                      open={isFilterPopoverOpen}
                      anchorEl={filterAnchorEl}
                      onClose={closeFilterPopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                      }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          width: { xs: '92vw', sm: 360 },
                          p: 2,
                          borderRadius: 2
                        }
                      }}
                    >
                      <Stack spacing={1.5}>
                        <FilterSelectInput
                          label="Site"
                          name="site_filter"
                          value={draftSite}
                          data={siteOptions}
                          onInputChange={setDraftSite}
                          sx={{ width: '100%', mr: 0 }}
                        />
                        {isExternal && (
                          <TextField
                            size="small"
                            label="Search Vehicle"
                            value={draftVehicle}
                            onChange={(e) => setDraftVehicle(e.target.value)}
                            placeholder={externalVehicleOptions.length
                              ? `e.g. ${externalVehicleOptions[0]}`
                              : 'Enter vehicle number'}
                            fullWidth
                          />
                        )}
                        <DateFilterField
                          label="Start Date Time *"
                          value={draftStartDateTime}
                          onlyDate={false}
                          onDateChange={(newValue) => {
                            setDraftStartDateTime(newValue);
                            setFilterError('');
                          }}
                          onClearFilter={() => setDraftStartDateTime(null)}
                          pickerProps={{
                            maxDateTime: draftEndDateTime || undefined
                          }}
                          sx={{ width: '100%', minWidth: '100%' }}
                        />
                        <DateFilterField
                          label="End Date Time *"
                          value={draftEndDateTime}
                          onlyDate={false}
                          onDateChange={(newValue) => {
                            setDraftEndDateTime(newValue);
                            setFilterError('');
                          }}
                          onClearFilter={() => setDraftEndDateTime(null)}
                          pickerProps={{
                            minDateTime: draftStartDateTime || undefined
                          }}
                          sx={{ width: '100%', minWidth: '100%' }}
                        />
                        {filterError && (
                          <Typography variant="caption" color="error">
                            {filterError}
                          </Typography>
                        )}
                        {isIssued && (
                          <FilterSelectInput
                            label="Tank"
                            name="tank_filter"
                            value={draftTank}
                            data={draftTankOptions}
                            onInputChange={setDraftTank}
                            sx={{ width: '100%', mr: 0 }}
                          />
                        )}
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleClearDraftFilters}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleApplyFilters}
                          sx={{ textTransform: 'none' }}
                        >
                          Apply
                        </Button>
                      </Stack>
                    </Popover>
                  </>
                ) : (
                  <>
                    <DateFilterField
                      label="Start Date Time *"
                      value={filterDateRange.start
                        ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
                        : null}
                      onlyDate={false}
                      onDateChange={(newValue) => setDateRangeField('start', newValue)}
                      onClearFilter={clearDateRangeFilter}
                      pickerProps={{
                        maxDateTime: filterDateRange.end
                          ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
                          : undefined
                      }}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 190,
                          lg: 220
                        },
                        minWidth: {
                          md: 180
                        }
                      }}
                    />
                    <DateFilterField
                      label="End Date Time *"
                      value={filterDateRange.end
                        ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
                        : null}
                      onlyDate={false}
                      onDateChange={(newValue) => setDateRangeField('end', newValue)}
                      onClearFilter={clearDateRangeFilter}
                      pickerProps={{
                        minDateTime: filterDateRange.start
                          ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
                          : undefined
                      }}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 190,
                          lg: 220
                        },
                        minWidth: {
                          md: 180
                        }
                      }}
                    />
                    {isAdmin && (
                    <FilterSelectInput
                      label="Site"
                      name="site"
                      value={siteFilter}
                      data={siteOptions}
                      onInputChange={setSiteFilter}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 160,
                          lg: 180
                        },
                        minWidth: {
                          md: 150
                        },
                        mr: 0
                      }}
                    />
                    )}
                    {isInward && canAddTransaction && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenModal(true)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        minHeight: 38,
                        px: 2,
                        borderRadius: 2,
                        width: { xs: '100%', sm: '100%', md: 'auto' },
                        flexShrink: 0
                      }}
                    >
                      Add Transaction
                    </Button>
                    )}
                  </>
                )}
              </FMSToolbar>
            </Stack>
          </Stack>

          {/* TOOLBAR (Everything inside same box) */}

          {/* TABLE */}
          <Box
            sx={{
              pt: 3,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <TransactionListTable
              refreshData={location.state?.fromDashboard}
              searchText={appliedSearchText}
              dateFilter={appliedDateFilter}
              siteFilter={appliedSiteFilter}
              tankFilter={isIssued && isAdmin ? tankFilter : 'all'}
              isSales={isSales}
              transactionTypeOverride={effectiveTransactionType}
            />
          </Box>

          {/* MODALS */}
          <NewTransaction
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ReportFilterModal
            open={openReportModal}
            title={getReportFormTitle(effectiveTransactionType)}
            formModel={activeReportFormModel}
            formValidation={activeReportValidation}
            formInitValues={activeReportInitialValues}
            filterConfig={isExternal ? externalReportFilterOptions : filterOptions}
            downloadUrl={reportDownloadUrl}
            handleClose={() => setOpenReportModal(false)}
          />

        </Container>
      </Box>
    </>
  );
};

Transactions.propTypes = {
  location: PropTypes.object
};

export default Transactions;
